import { CountOrStatus } from './CountOrStatus';
import { defineMessages, useIntl } from 'react-intl';
import { List } from './List';
import { ListItem } from './ListItem';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { SequenceType, Test, ActiveVariantType } from '../types';
import { useSequenceCounts } from './useSequenceCounts';
import * as React from 'react';
import clsx from 'clsx';

const messages = defineMessages({
  short: {
    id: 'omicsExplorer.sequenceCounts.short',
    defaultMessage: 'Short',
  },
  structrual: {
    id: 'omicsExplorer.sequenceCounts.structrual',
    defaultMessage: 'Structural',
  },
  copyNumber: {
    id: 'omicsExplorer.sequenceCounts.copyNumber',
    defaultMessage: 'Copy Number',
  },
  expression: {
    id: 'omicsExplorer.sequenceCounts.expression',
    defaultMessage: 'Expression',
  },
  selectAType: {
    id: 'omicsExplorer.sequenceCounts.selectAType',
    defaultMessage: 'Select a type',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  listItemSpacing: {
    margin: theme.spacing(0, 2.5, 0, 0),
    '&:last-child': {
      marginRight: 0,
    },
  },
}));

interface SequenceCountsProps {
  activeTest?: Test;
  className?: string;
  selectedActiveVariantType: ActiveVariantType;
  selectedSequenceType?: SequenceType;
  handleVariantTypeClick?: (newVariantType: ActiveVariantType) => any;
}

export const SequenceCounts: React.FC<SequenceCountsProps> = ({
  activeTest,
  className,
  selectedActiveVariantType,
  selectedSequenceType,
  handleVariantTypeClick,
}) => {
  const classes = useStyles({});

  const { formatMessage } = useIntl();

  const shortVariantSet = activeTest?.sets?.find(
    set =>
      set.sequenceType === selectedSequenceType &&
      set.setType === 'shortVariant',
  );
  const copyNumberSet = activeTest?.sets?.find(
    set =>
      set.sequenceType === selectedSequenceType &&
      set.setType === 'copyNumberVariant',
  );
  const structuralSet = activeTest?.sets?.find(
    set =>
      set.sequenceType === selectedSequenceType &&
      set.setType === 'structuralVariant',
  );
  const expressionSet = activeTest?.sets?.find(
    set =>
      set.sequenceType === selectedSequenceType && set.setType === 'expression',
  );

  const {
    isFetchingShortVariantCounts,
    isFetchingCopyNumberCounts,
    isFetchingStructuralCounts,
    isFetchingExpressionCounts,
    shortVariantsCount,
    expressionCount,
    structuralCount,
    copyNumberCount,
  } = useSequenceCounts({
    shortVariantSetId: shortVariantSet?.id,
    copyNumberSetId: copyNumberSet?.id,
    structuralSetId: structuralSet?.id,
    expressionSetId: expressionSet?.id,
  });

  return (
    <List
      aria-label={formatMessage(messages.selectAType)}
      className={clsx(classes.root, className)}
    >
      <ListItem
        className={classes.listItemSpacing}
        disabled={isFetchingShortVariantCounts}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onClick={() => handleVariantTypeClick('short')}
        isSelected={selectedActiveVariantType === 'short'}
      >
        <CountOrStatus
          label={formatMessage(messages.short)}
          isFetching={isFetchingShortVariantCounts}
          pillLabel={shortVariantsCount}
          isSelected={selectedActiveVariantType === 'short'}
          isIndexing={shortVariantSet?.status === 'INDEXING'}
        />
      </ListItem>
      <ListItem
        className={classes.listItemSpacing}
        disabled={isFetchingStructuralCounts}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onClick={() => handleVariantTypeClick('structural')}
        isSelected={selectedActiveVariantType === 'structural'}
      >
        <CountOrStatus
          label={formatMessage(messages.structrual)}
          isFetching={isFetchingStructuralCounts}
          pillLabel={structuralCount}
          isSelected={selectedActiveVariantType === 'structural'}
          isIndexing={structuralSet?.status === 'INDEXING'}
        />
      </ListItem>
      <ListItem
        className={classes.listItemSpacing}
        disabled={isFetchingCopyNumberCounts}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onClick={() => handleVariantTypeClick('copyNumber')}
        isSelected={selectedActiveVariantType === 'copyNumber'}
      >
        <CountOrStatus
          label={formatMessage(messages.copyNumber)}
          isFetching={isFetchingCopyNumberCounts}
          pillLabel={copyNumberCount}
          isSelected={selectedActiveVariantType === 'copyNumber'}
          isIndexing={copyNumberSet?.status === 'INDEXING'}
        />
      </ListItem>
      <ListItem
        className={classes.listItemSpacing}
        disabled={isFetchingExpressionCounts}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onClick={() => handleVariantTypeClick('expression')}
        isSelected={selectedActiveVariantType === 'expression'}
      >
        <CountOrStatus
          label={formatMessage(messages.expression)}
          isFetching={isFetchingExpressionCounts}
          pillLabel={expressionCount}
          isSelected={selectedActiveVariantType === 'expression'}
          isIndexing={expressionSet?.status === 'INDEXING'}
        />
      </ListItem>
    </List>
  );
};
